// Programmatic PDF export for the ComparisonTable.
// Builds charts and tables from raw scenario data — no DOM capture.
//
// Page layout (fit-to-page — no overflow):
//   Page 1 — Snapshot:        Est. Future Value Economic Difference + Strategies Applied + Current Year Snapshot
//   Page 2 — Cost Benefit:    Cost Benefit Analysis (N-year horizon) — its own page. The paired
//                             Year-1 / Year-N bars, or the per-strategy journey cards when the
//                             screen shows those (ExportView.journey)
//   Page 3 — Roth Growth:     All Roth 10-year projection line charts on one page (skipped if no Roth conversions)
//   Page 4 — Details:         Detailed Comparison, then Alt Key Takeaways
//                             (each spills to a fresh page with header when it doesn't fit)
// Every page carries a CYA footer.
//
// The file prints what the comparison page shows (Drew, 2026-10-05): the call site hands
// over the flags it rendered under as an ExportView, and every figure comes from the same
// helpers the screen calls (buildStrategyJourney).

import {
  payoffHorizonYears, journeyHorizon, altGrossReturnMultiple, altAnnualizedReturn,
  buildStrategyJourney, JOURNEY_ROTH_SPREAD_YEARS,
  type StrategyJourney,
} from "@/tax/strategyJourney";
import { LEGACY_EXPORT_VIEW, type ExportView } from "@/lib/exportView";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { TaxScenario } from "@/tax/types";
import { formatCurrency, formatPercent, formatMultiple, formatCompactCurrency, formatChartCurrency } from "@/tax/format";
import { METRICS } from "@/lib/comparisonMetrics";
import { buildStrategyList } from "@/lib/strategyList";
import { rothYearByYearSeries } from "@/tax/rothGrowth";
import { CYA_TEXT } from "@/lib/disclaimer";

// Brand colors
const NAVY: [number, number, number] = [45, 75, 110];
const BLUE: [number, number, number] = [33, 150, 243];
const GREEN: [number, number, number] = [46, 125, 50];
const RED: [number, number, number] = [198, 40, 40];
const GRAY: [number, number, number] = [120, 120, 120];
const WHITE: [number, number, number] = [255, 255, 255];

// Chart bar colors (fixed palette matching COMPARE_COLORS order)
const BAR_COLORS: [number, number, number][] = [
  [...BLUE],
  [...NAVY],
  [245, 166, 35],  // amber/fica
  [156, 39, 176],  // purple/amt
  [198, 40, 40],   // red/niit
];

// Line chart colors for Roth projection (match RothProjectionChart on-screen palette)
const ROTH_LINE: [number, number, number] = [33, 150, 243];   // blue
const IRA_CURRENT_LINE: [number, number, number] = [120, 120, 120];
const IRA_HIGHER_LINE: [number, number, number] = [198, 40, 40];

const PAGE_W = 215.9; // letter width mm
const PAGE_H = 279.4; // letter height mm
const MARGIN = 12.7;  // 0.5 in
const CONTENT_W = PAGE_W - 2 * MARGIN;
const FOOTER_HEIGHT = 14; // reserved at the bottom for CYA disclaimer

// Default future-tax-rate bump used on screen (5pp). Mirrored here so the PDF tells the same story.
const DEFAULT_TAX_RATE_DELTA = 0.05;

// CYA disclaimer — Drew owns the final copy. Single source in disclaimer.ts
// (imported above), shared with the in-app Strategy Story presentation.

function formatVal(value: number, format: "currency" | "percent") {
  return format === "currency" ? formatCurrency(value) : formatPercent(value);
}

function shortLabel(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9 _-]/g, "").replace(/\s+/g, "-");
}

// Strip the client-name prefix from a scenario name so column headers stay narrow.
// Mirrors on-screen ComparisonTable.tsx#strategySuffix — grabs the LAST parenthesized
// group at end of the name. Resilient to baseline-name drift (duplicated clients,
// renamed baselines) where prefix-matching against scenarios[0].input.name would fail.
// "Professor Baseline (Roth + Alt + LTCG)" → "Roth + Alt + LTCG".
function shortStrategyName(fullName: string, _clientName: string, isBaseline: boolean): string {
  if (isBaseline) return "Baseline";
  const m = fullName.match(/\(([^)]+)\)\s*$/);
  return m ? m[1] : fullName;
}

// ── Header ──────────────────────────────────────────────────────────────────
function drawHeader(doc: jsPDF, clientName: string, reportDate: string) {
  const y = MARGIN;
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text(`TaxGenie Comparison Report — ${clientName}`, PAGE_W / 2, y + 5, { align: "center" });
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...GRAY);
  doc.text(`Generated ${reportDate}`, PAGE_W / 2, y + 11, { align: "center" });
  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y + 14, PAGE_W - MARGIN, y + 14);
  return y + 19; // return cursor Y after header
}

// ── Footer (CYA) ─────────────────────────────────────────────────────────────
// Drawn last on every page so it sits at the bottom regardless of body content.
function drawCYAFooter(doc: jsPDF) {
  const top = PAGE_H - MARGIN - FOOTER_HEIGHT + 2;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, top, PAGE_W - MARGIN, top);
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...GRAY);
  const lines = doc.splitTextToSize(CYA_TEXT, CONTENT_W);
  doc.text(lines, MARGIN, top + 3);
}

// ── Bar Chart Drawing ────────────────────────────────────────────────────────
type RGB = [number, number, number];
type SeriesColor = RGB | RGB[]; // single RGB = same color for all categories; RGB[] = per-category

function resolveSeriesColor(sc: SeriesColor, catIdx: number): RGB {
  if (Array.isArray(sc[0])) return (sc as RGB[])[catIdx % (sc as RGB[]).length];
  return sc as RGB;
}

function drawBarChart(
  doc: jsPDF,
  startY: number,
  title: string,
  categories: string[],
  seriesNames: string[],
  seriesData: number[][],  // seriesData[seriesIdx][categoryIdx]
  chartHeight: number = 55,
  colors?: SeriesColor[],
  topLabels?: ({ text: string; color: RGB } | null)[],
): number {
  const palette: SeriesColor[] = colors ?? BAR_COLORS;
  let y = startY;

  // Title
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text(title, MARGIN, y + 4);
  y += 8;

  const chartLeft = MARGIN + 20; // room for Y-axis labels
  const chartRight = PAGE_W - MARGIN - 5;
  const chartWidth = chartRight - chartLeft;
  const chartBottom = y + chartHeight;
  const chartTop = y + 2;
  const plotHeight = chartBottom - chartTop;

  // Find max value for scale — extra headroom if topLabels are drawn above bars
  const allValues = seriesData.flat();
  const maxVal = Math.max(...allValues, 1);
  const headroomFactor = topLabels ? 1.3 : 1.15;
  const niceMax = Math.ceil((maxVal * headroomFactor) / 10000) * 10000 || (maxVal * headroomFactor);

  // Y-axis gridlines + labels
  const numGridLines = 4;
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...GRAY);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.15);
  for (let i = 0; i <= numGridLines; i++) {
    const val = (niceMax / numGridLines) * i;
    const lineY = chartBottom - (plotHeight * (val / niceMax));
    doc.line(chartLeft, lineY, chartRight, lineY);
    doc.text(shortLabel(val), chartLeft - 2, lineY + 1, { align: "right" });
  }

  // Bars
  const numCategories = categories.length;
  const numSeries = seriesNames.length;
  const categoryWidth = chartWidth / numCategories;
  const barGroupWidth = categoryWidth * 0.7;
  const barWidth = barGroupWidth / numSeries;
  const barGap = (categoryWidth - barGroupWidth) / 2;

  for (let catIdx = 0; catIdx < numCategories; catIdx++) {
    const catX = chartLeft + catIdx * categoryWidth + barGap;

    for (let sIdx = 0; sIdx < numSeries; sIdx++) {
      const value = seriesData[sIdx][catIdx];
      const barH = (value / niceMax) * plotHeight;
      const barX = catX + sIdx * barWidth;
      const barY = chartBottom - barH;

      const color = resolveSeriesColor(palette[sIdx % palette.length], catIdx);
      doc.setFillColor(...color);
      doc.rect(barX, barY, barWidth - 0.5, barH, "F");

      // Value label above bar
      if (value > 0) {
        doc.setFontSize(6);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...color);
        doc.text(shortLabel(value), barX + (barWidth - 0.5) / 2, barY - 1, { align: "center" });
      }
    }

    // Category label
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    doc.text(categories[catIdx], chartLeft + catIdx * categoryWidth + categoryWidth / 2, chartBottom + 4, { align: "center" });
  }

  // Top labels (e.g. net benefit per category)
  if (topLabels) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    for (let catIdx = 0; catIdx < numCategories; catIdx++) {
      const lbl = topLabels[catIdx];
      if (!lbl) continue;
      const cx = chartLeft + (catIdx + 0.5) * categoryWidth;
      doc.setTextColor(...lbl.color);
      doc.text(lbl.text, cx, chartTop + 3, { align: "center" });
    }
  }

  // Legend
  const legendY = chartBottom + 8;
  let legendX = chartLeft;
  doc.setFontSize(7);
  for (let sIdx = 0; sIdx < numSeries; sIdx++) {
    const sColor = palette[sIdx % palette.length];
    const isPerCategory = Array.isArray(sColor[0]);
    if (isPerCategory) {
      const stripColors = sColor as RGB[];
      const stripW = 3 / stripColors.length;
      for (let ci = 0; ci < stripColors.length; ci++) {
        doc.setFillColor(...stripColors[ci]);
        doc.rect(legendX + ci * stripW, legendY - 2, stripW, 3, "F");
      }
    } else {
      doc.setFillColor(...(sColor as RGB));
      doc.rect(legendX, legendY - 2, 3, 3, "F");
    }
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "normal");
    doc.text(seriesNames[sIdx], legendX + 4, legendY + 0.5);
    legendX += doc.getTextWidth(seriesNames[sIdx]) + 8;
  }

  return legendY + 6;
}

// ── Line Chart Drawing (Roth projection) ─────────────────────────────────────
// Draws an N-year line chart. Each series has its own color/dash. Sized to the
// caller-supplied height so we can pack multiple charts on Page 2 without spill.
function drawLineChart(
  doc: jsPDF,
  startY: number,
  title: string,
  subtitle: string | null,
  xLabels: string[],
  series: { name: string; values: number[]; color: RGB; dashed?: boolean }[],
  chartHeight: number,
): number {
  let y = startY;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text(title, MARGIN, y + 4);
  y += 5;
  if (subtitle) {
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
    doc.text(subtitle, MARGIN, y + 3);
    y += 4;
  }
  y += 2;

  const chartLeft = MARGIN + 20;
  const chartRight = PAGE_W - MARGIN - 5;
  const chartWidth = chartRight - chartLeft;
  const chartTop = y;
  const chartBottom = y + chartHeight;
  const plotHeight = chartBottom - chartTop;

  const allValues = series.flatMap((s) => s.values);
  const maxVal = Math.max(...allValues, 1);
  const niceMax = Math.ceil((maxVal * 1.1) / 10000) * 10000 || maxVal * 1.1;

  // Y gridlines
  const numGridLines = 4;
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...GRAY);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.15);
  for (let i = 0; i <= numGridLines; i++) {
    const val = (niceMax / numGridLines) * i;
    const lineY = chartBottom - plotHeight * (val / niceMax);
    doc.line(chartLeft, lineY, chartRight, lineY);
    doc.text(shortLabel(val), chartLeft - 2, lineY + 1, { align: "right" });
  }

  // X labels — thin them out so they don't collide at small heights
  const n = xLabels.length;
  const xStep = chartWidth / Math.max(1, n - 1);
  const xLabelEvery = n <= 6 ? 1 : n <= 12 ? 2 : 3;
  doc.setFontSize(6.5);
  doc.setTextColor(60, 60, 60);
  for (let i = 0; i < n; i++) {
    if (i % xLabelEvery !== 0 && i !== n - 1) continue;
    const x = chartLeft + i * xStep;
    doc.text(xLabels[i], x, chartBottom + 3.5, { align: "center" });
  }

  // Series lines
  doc.setLineWidth(0.6);
  for (const s of series) {
    doc.setDrawColor(...s.color);
    if (s.dashed) {
      doc.setLineDashPattern([1.2, 1.2], 0);
    } else {
      doc.setLineDashPattern([], 0);
    }
    for (let i = 1; i < s.values.length; i++) {
      const x0 = chartLeft + (i - 1) * xStep;
      const y0 = chartBottom - plotHeight * (s.values[i - 1] / niceMax);
      const x1 = chartLeft + i * xStep;
      const y1 = chartBottom - plotHeight * (s.values[i] / niceMax);
      doc.line(x0, y0, x1, y1);
    }
  }
  doc.setLineDashPattern([], 0);

  // Endpoint value labels (rightmost point of each series).
  // De-collide vertically: sort by natural Y, then push labels apart so they don't stack
  // when two series end at nearly the same value (e.g. IRA-current vs IRA-higher).
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "bold");
  const lastIdx = series[0]?.values.length - 1;
  const minLabelGap = 3;
  const endpointLabels = series
    .map((s) => {
      const v = s.values[lastIdx];
      return {
        text: shortLabel(v),
        color: s.color,
        y: chartBottom - plotHeight * (v / niceMax),
      };
    })
    .sort((a, b) => a.y - b.y);
  for (let i = 1; i < endpointLabels.length; i++) {
    if (endpointLabels[i].y - endpointLabels[i - 1].y < minLabelGap) {
      endpointLabels[i].y = endpointLabels[i - 1].y + minLabelGap;
    }
  }
  const labelX = chartLeft + lastIdx * xStep + 1;
  for (const lbl of endpointLabels) {
    doc.setTextColor(...lbl.color);
    doc.text(lbl.text, labelX, lbl.y - 0.5);
  }

  // Legend
  const legendY = chartBottom + 7.5;
  let legendX = chartLeft;
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  for (const s of series) {
    doc.setDrawColor(...s.color);
    doc.setLineWidth(0.8);
    if (s.dashed) doc.setLineDashPattern([1.2, 1.2], 0); else doc.setLineDashPattern([], 0);
    doc.line(legendX, legendY - 1, legendX + 5, legendY - 1);
    doc.setLineDashPattern([], 0);
    doc.setTextColor(60, 60, 60);
    doc.text(s.name, legendX + 6.5, legendY + 0.5);
    legendX += doc.getTextWidth(s.name) + 13;
  }

  return legendY + 5;
}

// ── Page 1: Snapshot ─────────────────────────────────────────────────────────
// Future Value Economic Difference table + Strategies Applied table + Current Year Snapshot chart
function drawPage1(doc: jsPDF, scenarios: TaxScenario[]) {
  const clientName = scenarios[0].input.name;
  const reportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  let y = drawHeader(doc, clientName, reportDate);

  const baseScenario = scenarios[0];
  const hasAnyRoth = scenarios.some((s) => (s.input.rothConversionAmount ?? 0) > 0);

  // ── Economic Difference Summary ──
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("Est. Future Value Economic Difference", MARGIN, y + 4);
  y += 7;

  const summaryHead = [["Strategy", "Net Benefit", "Tax Savings", "Alt Inv. Returns", "Future Taxes Avoided"]];
  const summaryBody: string[][] = [];
  for (let i = 0; i < scenarios.length; i++) {
    const s = scenarios[i];
    if (i === 0) {
      summaryBody.push([shortStrategyName(s.input.name, clientName, true), "—", "—", "—", "—"]);
      continue;
    }
    const taxSavings = baseScenario.results.totalTaxLiability - s.results.totalTaxLiability;
    const investmentReturns = s.results.hpeFutureValue > 0 ? s.results.hpeFutureValue - s.input.alternativeInvestmentAmount : 0;
    const futureTaxesAvoided = s.results.estimatedFutureTaxAvoided;
    const netBenefit = taxSavings + investmentReturns + futureTaxesAvoided;
    summaryBody.push([
      shortStrategyName(s.input.name, clientName, false),
      formatCurrency(netBenefit),
      formatCurrency(taxSavings),
      formatCurrency(investmentReturns),
      formatCurrency(futureTaxesAvoided),
    ]);
  }

  autoTable(doc, {
    startY: y,
    head: summaryHead,
    body: summaryBody,
    theme: "grid",
    headStyles: { fillColor: NAVY, textColor: WHITE, fontSize: 8, fontStyle: "bold" },
    bodyStyles: { fontSize: 8 },
    columnStyles: {
      0: { fontStyle: "bold" },
      1: { halign: "right", fontStyle: "bold" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
    },
    didParseCell(data) {
      // Color net benefit green/red
      if (data.section === "body" && data.column.index === 1 && data.cell.text[0] !== "—") {
        const raw = data.cell.text[0];
        const isNeg = raw.startsWith("-") || raw.startsWith("($") || raw.startsWith("(");
        data.cell.styles.textColor = isNeg ? (RED) : (GREEN);
      }
    },
    margin: { left: MARGIN, right: MARGIN },
  });

  y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // ── Strategies Applied ──
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("Strategies Applied", MARGIN, y + 4);
  y += 7;

  const strategyHead = [["Strategy", "Applied"]];
  const strategyBody: string[][] = scenarios.map((s, i) => {
    if (i === 0) return [shortStrategyName(s.input.name, clientName, true), "No changes"];
    const items = buildStrategyList(s.input, baseScenario.input);
    const text = items.length === 0 ? "Matches baseline" : items.map((it) => `${it.label}: ${it.value}`).join("; ");
    return [shortStrategyName(s.input.name, clientName, false), text];
  });

  autoTable(doc, {
    startY: y,
    head: strategyHead,
    body: strategyBody,
    theme: "grid",
    headStyles: { fillColor: NAVY, textColor: WHITE, fontSize: 8, fontStyle: "bold" },
    bodyStyles: { fontSize: 8 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 55 },
      1: { cellWidth: "auto" },
    },
    margin: { left: MARGIN, right: MARGIN },
  });

  y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // ── Current Year Snapshot Chart ──
  // Scale chart height to fill the remaining space above the footer so it always sits cleanly.
  const footerTop = PAGE_H - MARGIN - FOOTER_HEIGHT;
  const reservedForChartChrome = 22; // title + legend below chart
  const chartHeight = Math.max(35, Math.min(80, footerTop - y - reservedForChartChrome));

  const categories1 = hasAnyRoth ? ["Total Tax", "Total Economic Cost", "Roth Conversion"] : ["Total Tax", "Total Economic Cost"];
  const seriesNames1 = scenarios.map((s, i) => shortStrategyName(s.input.name, clientName, i === 0));
  const seriesData1 = scenarios.map((s) => {
    const row = [s.results.totalTaxLiability, s.results.totalEconomicCost];
    if (hasAnyRoth) row.push(s.input.rothConversionAmount ?? 0);
    return row;
  });

  drawBarChart(doc, y, "Current Year Snapshot", categories1, seriesNames1, seriesData1, chartHeight);
}

// ── Page 2: Cost Benefit (N-Year Horizon) ───────────────────────────────────
// Dedicated page for the N-year horizon Cost Benefit Analysis so it reads as the
// centerpiece of the projection story. Draws whichever the screen's card shows:
// the journey cards when `cost_benefit_journey` is on, else the paired bars.
async function drawPage2(doc: jsPDF, scenarios: TaxScenario[], view: ExportView) {
  doc.addPage();
  const clientName = scenarios[0].input.name;
  const reportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  let y = drawHeader(doc, clientName, reportDate);

  const baseScenario = scenarios[0];
  const growthYears = payoffHorizonYears(scenarios);

  if (view.journey) {
    await drawJourneyCards(doc, y, scenarios, growthYears);
    return;
  }

  const strategyScenarios = scenarios.slice(1);
  const categories2 = strategyScenarios.map((s) => shortStrategyName(s.input.name, clientName, false));
  const costData = strategyScenarios.map((s) => s.results.totalEconomicCost - baseScenario.results.totalEconomicCost);
  const returnData = strategyScenarios.map((s) => {
    const altReturned = s.results.hpeFutureValue || 0;
    const futureTaxAvoided = s.results.estimatedFutureTaxAvoided || 0;
    return altReturned + futureTaxAvoided;
  });
  const returnColors: [number, number, number][] = strategyScenarios.map(
    (_, i) => BAR_COLORS[(i + 1) % BAR_COLORS.length],
  );
  const netBenefitLabels = strategyScenarios.map((_, i) => {
    const net = returnData[i] - costData[i];
    return {
      text: `${net >= 0 ? "+" : "-"}${shortLabel(Math.abs(net))}`,
      color: (net >= 0 ? GREEN : RED) as RGB,
    };
  });

  // Larger chart now that it owns the page.
  y = drawBarChart(
    doc, y,
    `Cost Benefit Analysis — ${growthYears} Year Horizon`,
    categories2,
    ["Year 1 Cost Over Baseline", `Year ${growthYears} Gross Return (colored by scenario)`],
    [costData, returnData],
    110,
    [BLUE, returnColors],
    netBenefitLabels,
  );

  // Caption under Cost Benefit chart
  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...GRAY);
  const caption1 = `Year 1 shows each strategy's out-of-pocket cost above baseline. Year ${growthYears} shows projected total gross return (estimated future taxes avoided on Roth + alt investment gross return). Green/red labels above each pair are the net benefit at year ${growthYears}.`;
  const captionLines = doc.splitTextToSize(caption1, CONTENT_W);
  doc.text(captionLines, MARGIN, y + 4);
}

// ── Journey cards (Cost Benefit view with `cost_benefit_journey` on) ─────────
// The charts are the screen's own: captureJourneyCharts() renders StrategyJourneyCards off
// screen and returns each chart's SVG, which svg2pdf.js places here as vector shapes — the
// same ticks, fill, curve and colours the advisor is looking at. The card frame, the name,
// the story line and the net figure around each chart are written here in the same words.
// No breakeven marker on paper: the line crossing zero says it (Drew, 2026-10-22).
const JOURNEY_CARD_GAP = 5;

// index.css light-theme tokens, as RGB, for the text and frame around each chart.
const TOKEN_SUCCESS: RGB = [45, 159, 106];      // --success 152 56% 40%
const TOKEN_DESTRUCTIVE: RGB = [220, 40, 40];   // --destructive 0 72% 51%
const TOKEN_MUTED_FG: RGB = [107, 114, 128];    // --muted-foreground 220 9% 46%
const TOKEN_BORDER: RGB = [223, 226, 231];      // --border 220 13% 89%

async function drawJourneyCards(doc: jsPDF, startY: number, scenarios: TaxScenario[], horizon: number) {
  const clientName = scenarios[0].input.name;
  const baseline = scenarios[0];
  const strategies = scenarios.slice(1);
  const journeys: StrategyJourney[] = strategies.map((s) => buildStrategyJourney(s, baseline, horizon));

  const [{ captureJourneyCharts }, { svg2pdf }] = await Promise.all([
    import("@/lib/journeyCapture"),
    import("svg2pdf.js"),
  ]);
  const charts = await captureJourneyCharts(scenarios, horizon);
  const chartFor = new Map(charts.map((c) => [c.scenarioId, c]));

  const cardW = (CONTENT_W - JOURNEY_CARD_GAP) / 2;
  const chartW = cardW - 4;
  const headerH = 14;
  const chartH = (c: { width: number; height: number } | undefined) => (c && c.width > 0 ? (chartW * c.height) / c.width : 50);
  const rowH = (i: number) =>
    headerH + Math.max(...strategies.slice(i, i + 2).map((st) => chartH(chartFor.get(st.id)))) + 3;
  const footerTop = PAGE_H - MARGIN - FOOTER_HEIGHT;

  const title = `Cost Benefit Analysis — ${horizon} Year Horizon`;
  let y = startY;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text(title, MARGIN, y + 4);
  y += 8;

  let cardH = 0;
  for (let i = 0; i < strategies.length; i++) {
    const col = i % 2;
    if (col === 0) {
      if (i > 0) y += cardH + JOURNEY_CARD_GAP;
      cardH = rowH(i);
      if (y + cardH > footerTop) {
        doc.addPage();
        const reportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
        y = drawHeader(doc, clientName, reportDate);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...NAVY);
        doc.text(`${title} (continued)`, MARGIN, y + 4);
        y += 8;
      }
    }
    const s = strategies[i];
    const j = journeys[i];
    const x0 = MARGIN + col * (cardW + JOURNEY_CARD_GAP);

    // Card: rounded, hairline border, as on screen
    doc.setDrawColor(...TOKEN_BORDER);
    doc.setLineWidth(0.25);
    doc.roundedRect(x0, y, cardW, cardH, 2, 2, "S");

    // Header: name, then the story line; the net figure on the right in the mono face.
    const costLabel =
      j.costOverBaseline >= 0
        ? `Net out of pocket ${formatCompactCurrency(j.costOverBaseline)}`
        : `Saves ${formatCompactCurrency(Math.abs(j.costOverBaseline))} in Year 1`;
    const breakevenLabel =
      j.breakevenYear === null
        ? `Not whole by Yr ${j.horizonYears}`
        : j.breakevenYear === 0
          ? "Ahead from day one"
          : `Whole again ~Yr ${Math.ceil(j.breakevenYear)}`;
    const net = `${j.netBenefit >= 0 ? "+" : "-"}${formatChartCurrency(Math.abs(j.netBenefit))}`;
    doc.setFont("courier", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...(j.netBenefit >= 0 ? TOKEN_SUCCESS : TOKEN_DESTRUCTIVE));
    doc.text(net, x0 + cardW - 3.5, y + 6, { align: "right" });
    const netW = doc.getTextWidth(net) + 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(20, 20, 20);
    doc.text(shortStrategyName(s.input.name, clientName, false), x0 + 3.5, y + 6, { maxWidth: cardW - netW - 7 });

    // Story line. The arrow is drawn, not typed: the built-in font has no arrow glyph,
    // and one non-Latin character makes viewers space out the whole line.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.6);
    doc.setTextColor(...TOKEN_MUTED_FG);
    const lineY = y + 10.3;
    let tx = x0 + 3.5;
    doc.text(costLabel, tx, lineY);
    tx += doc.getTextWidth(costLabel) + 0.9;
    doc.setDrawColor(...TOKEN_MUTED_FG);
    doc.setLineWidth(0.18);
    const ay = lineY - 0.8;
    doc.line(tx, ay, tx + 2.6, ay);
    doc.line(tx + 2.6, ay, tx + 1.9, ay - 0.55);
    doc.line(tx + 2.6, ay, tx + 1.9, ay + 0.55);
    tx += 3.5;
    doc.text(`${formatCompactCurrency(j.grossReturn)} back by Yr ${j.horizonYears} · ${breakevenLabel}`, tx, lineY);

    // The screen's chart, as vector shapes
    const chart = chartFor.get(s.id);
    if (chart) {
      await svg2pdf(chart.svg, doc, { x: x0 + 2, y: y + headerH, width: chartW, height: chartH(chart) });
    }
  }

  // Caption — the screen's wording under the journey cards.
  y += cardH + 5;
  if (y + 12 > footerTop) {
    doc.addPage();
    const reportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    y = drawHeader(doc, clientName, reportDate);
  }
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...TOKEN_MUTED_FG);
  const caption =
    `Each card tracks one strategy's cumulative position vs. doing nothing, from its scenario's real payback schedule: ` +
    `the line starts down by the Year 1 cost over baseline (out-of-pocket net of that year's tax savings), alt investment cash ` +
    `follows its hypothetical schedule scaled to the investment, and estimated future taxes avoided on the Roth accrue in equal ` +
    `slices over the first ${JOURNEY_ROTH_SPREAD_YEARS} years (assumes steady growth) and then hold. Red = still out of pocket; green = ahead of baseline.`;
  doc.text(doc.splitTextToSize(caption, CONTENT_W), MARGIN, y + 4);
}

// ── Page 3: Roth Growth Projections ─────────────────────────────────────────
// All Roth projection line charts on a single page. Per-chart height scales to
// fit the strategy count so they all sit cleanly above the CYA footer.
// Skipped entirely when no scenarios have a Roth conversion.
function drawRothPage(doc: jsPDF, scenarios: TaxScenario[]) {
  const baseScenario = scenarios[0];
  const rothScenarios = scenarios.slice(1).filter((s) => (s.input.rothConversionAmount ?? 0) > 0);
  if (rothScenarios.length === 0) return;

  doc.addPage();
  const clientName = scenarios[0].input.name;
  const reportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  let y = drawHeader(doc, clientName, reportDate);

  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("Roth Growth — 10 Year Projection", MARGIN, y + 4);
  y += 6;
  // Page-level explainer so each per-strategy subtitle stays short.
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...GRAY);
  const intro =
    "Each chart compares a Roth conversion (taxed today, then tax-free forever) to keeping the money in a Traditional IRA (taxed when withdrawn). The two IRA lines show what an after-tax IRA balance is worth if tax rates stay flat vs. rise.";
  const introLines = doc.splitTextToSize(intro, CONTENT_W);
  doc.text(introLines, MARGIN, y + 3);
  y += introLines.length * 3.5 + 4;

  const footerTop = PAGE_H - MARGIN - FOOTER_HEIGHT;
  const available = footerTop - y;
  const chrome = 25;           // title + subtitle + x-axis labels + legend
  const gap = 4;
  const n = rothScenarios.length;
  // Solve per-chart plot height to fit N charts on this page. Clamp to a readable range.
  const plotH = Math.max(
    18,
    Math.min(100, (available - (n - 1) * gap) / n - chrome),
  );

  const baselineETR = baseScenario.results.effectiveTaxRate ?? 0;
  const PROJECTION_YEARS = 10;
  const xLabels = Array.from({ length: PROJECTION_YEARS + 1 }, (_, i) => `Y${i}`);

  for (const s of rothScenarios) {
    const principal = s.input.rothConversionAmount ?? 0;
    const rothSeries = rothYearByYearSeries(
      principal,
      s.input.rothGrowthModel,
      s.input.rothGrowthRate ?? 0,
      PROJECTION_YEARS,
    );
    const etrCur = Math.max(0, Math.min(1, baselineETR));
    const etrHigh = Math.max(0, Math.min(1, baselineETR + DEFAULT_TAX_RATE_DELTA));
    const iraCurrent = rothSeries.map((v) => v * (1 - etrCur));
    const iraHigher = rothSeries.map((v) => v * (1 - etrHigh));

    const stratLabel = shortStrategyName(s.input.name, clientName, false);
    const subtitle = `Conversion ${formatCurrency(principal)} · Future scenario assumes income-tax rates rise 5 percentage points (from ${formatPercent(etrCur)} today to ${formatPercent(etrCur + DEFAULT_TAX_RATE_DELTA)})`;
    y = drawLineChart(
      doc, y,
      stratLabel,
      subtitle,
      xLabels,
      [
        { name: "Roth — tax-free in retirement", values: rothSeries, color: ROTH_LINE },
        { name: `IRA — taxed at today's rate (${formatPercent(etrCur)})`, values: iraCurrent, color: IRA_CURRENT_LINE },
        { name: `IRA — taxed at future rate (${formatPercent(etrCur + DEFAULT_TAX_RATE_DELTA)})`, values: iraHigher, color: IRA_HIGHER_LINE, dashed: true },
      ],
      plotH,
    );
    y += gap;
  }
}

// ── Page 4 (Details): Detailed Comparison ───────────────────────────────────
// Returns the cursor below the table so Alt Key Takeaways follows it.
function drawDetailsPage(doc: jsPDF, scenarios: TaxScenario[]): number {
  doc.addPage();
  const clientName = scenarios[0].input.name;
  const reportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  let y = drawHeader(doc, clientName, reportDate);

  const baseScenario = scenarios[0];
  const growthYears = payoffHorizonYears(scenarios);

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("Detailed Comparison", MARGIN, y + 4);
  y += 7;

  // Short strategy tags keep columns narrow; full names live in the Scenario Key legend above.
  const shortNames = scenarios.map((s, i) => shortStrategyName(s.input.name, clientName, i === 0));
  const head = ["Metric", ...shortNames];
  if (scenarios.length > 1) {
    for (let i = 1; i < scenarios.length; i++) {
      head.push(`Diff ${shortNames[i]}`);
    }
  }

  // Build body rows — hide optional metrics that are zero across all scenarios
  const visibleMetrics = METRICS.filter(m =>
    !m.optional || scenarios.some(s => m.getValue(s) !== 0)
  );
  type PdfCell = string | { content: string; colSpan?: number; styles?: Record<string, unknown> };
  const body: PdfCell[][] = [];
  const highlightRows: number[] = [];
  const metricIdxByRow = new Map<number, number>();

  const totalCols = 1 + scenarios.length + Math.max(0, scenarios.length - 1);
  const firstFutureKey = visibleMetrics.find((m) => m.group === "future")?.key;
  const sectionStyles = {
    fillColor: [240, 240, 240],
    textColor: [90, 90, 90],
    fontStyle: "bold",
    fontSize: 7,
  };

  body.push([{ content: "Current Year", colSpan: totalCols, styles: sectionStyles }]);

  for (let mIdx = 0; mIdx < visibleMetrics.length; mIdx++) {
    const m = visibleMetrics[mIdx];
    if (m.key === firstFutureKey) {
      body.push([{
        content: `Projected at end of Year ${growthYears}`,
        colSpan: totalCols,
        styles: sectionStyles,
      }]);
    }
    if (m.highlight) highlightRows.push(body.length);
    metricIdxByRow.set(body.length, mIdx);
    const row: PdfCell[] = [m.label];
    for (const s of scenarios) {
      row.push(formatVal(m.getValue(s), m.format));
    }
    for (const s of scenarios.slice(1)) {
      const diff = m.getValue(s) - m.getValue(baseScenario);
      if (Math.abs(diff) < 0.01) {
        row.push("—");
      } else {
        const prefix = diff > 0 ? "+" : "";
        row.push(`${prefix}${formatVal(diff, m.format)}`);
      }
    }
    body.push(row);
  }

  highlightRows.push(body.length);
  const fvRow = ["Est. Future Value Econ. Diff."];
  for (let i = 0; i < scenarios.length; i++) {
    const s = scenarios[i];
    if (i === 0) {
      fvRow.push("—");
      continue;
    }
    const oopDiff = s.results.totalEconomicCost - baseScenario.results.totalEconomicCost;
    const fvEconDiff = s.results.hpeFutureValue + s.results.estimatedFutureTaxAvoided - oopDiff;
    fvRow.push(formatCurrency(fvEconDiff));
  }
  for (const s of scenarios.slice(1)) {
    const oopDiff = s.results.totalEconomicCost - baseScenario.results.totalEconomicCost;
    const fvEconDiff = s.results.hpeFutureValue + s.results.estimatedFutureTaxAvoided - oopDiff;
    fvRow.push(formatCurrency(fvEconDiff));
  }
  body.push(fvRow);

  const numScenarios = scenarios.length;

  // Auto-shrink font + metric column as the column count grows so wide numbers
  // ($1,184,110, +$472,606) stay on one line instead of wrapping mid-digit.
  // Each scenario adds a value col AND a diff col, so column count = 1 + 2*(N-1) + 1.
  const detailFontSize = totalCols >= 9 ? 6 : totalCols >= 7 ? 6.5 : 7;
  const metricColW = totalCols >= 9 ? 30 : totalCols >= 7 ? 36 : 42;
  const cellPadH = totalCols >= 9 ? 0.6 : 1;

  autoTable(doc, {
    startY: y,
    head: [head],
    body,
    theme: "grid",
    styles: { cellPadding: { top: 1.2, right: cellPadH, bottom: 1.2, left: cellPadH }, overflow: "linebreak" },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontSize: detailFontSize, fontStyle: "bold", halign: "center" },
    bodyStyles: { fontSize: detailFontSize },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: metricColW },
      ...Object.fromEntries(
        Array.from({ length: totalCols - 1 }, (_, i) => [i + 1, { halign: "right" as const, cellWidth: "auto" as const }])
      ),
    },
    // Reserve room for footer + Alt Key Takeaways below.
    margin: { left: MARGIN, right: MARGIN, bottom: MARGIN + FOOTER_HEIGHT },
    didParseCell(data) {
      if (data.section !== "body") return;
      const rowIdx = data.row.index;

      if (!metricIdxByRow.has(rowIdx) && rowIdx !== body.length - 1) return;

      if (highlightRows.includes(rowIdx)) {
        data.cell.styles.fillColor = [232, 240, 254];
        data.cell.styles.fontStyle = "bold";
      }

      const deltaStartCol = 1 + numScenarios;
      if (data.column.index >= deltaStartCol) {
        const text = data.cell.text[0];
        if (text && text !== "—") {
          const mIdx = metricIdxByRow.get(rowIdx);
          const isPositive = text.startsWith("+");
          if (mIdx !== undefined) {
            const direction = visibleMetrics[mIdx].direction;
            const isGood = direction === "lower-is-better" ? !isPositive : isPositive;
            data.cell.styles.textColor = isGood ? (GREEN) : (RED);
          }
          if (rowIdx === body.length - 1) {
            const isNeg = text.startsWith("-") || text.startsWith("($");
            data.cell.styles.textColor = isNeg ? (RED) : (GREEN);
          }
        }
        data.cell.styles.fillColor = [248, 248, 248];
      }
    },
  });

  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
}

// ── Alt Key Takeaways (after the detail table) ──────────
function drawAltKeyTakeaways(doc: jsPDF, scenarios: TaxScenario[], startY: number) {
  const baseScenario = scenarios[0];
  const clientName = baseScenario.input.name;
  const hpeScenarios = scenarios.filter((s) => s.input.alternativeInvestmentAmount > 0);
  if (hpeScenarios.length === 0) return;

  let y = startY + 8;

  // If the detailed comparison filled page 3, push to a fresh page with header
  // instead of squishing the section title into the footer band.
  const footerTop = PAGE_H - MARGIN - FOOTER_HEIGHT;
  // Title (7) + table header (8) + min 3 rows (8 each) + padding ≈ 40mm minimum.
  const minNeeded = 40;
  if (y + minNeeded > footerTop) {
    doc.addPage();
    const reportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    y = drawHeader(doc, clientName, reportDate);
  }

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("Alt Key Takeaways", MARGIN, y + 4);
  y += 7;

  const head = ["Metric", ...hpeScenarios.map((s) => shortStrategyName(s.input.name, clientName, false))];
  const showFutureTaxes = hpeScenarios.some((s) => s.results.estimatedFutureTaxAvoided > 0);

  const body: string[][] = [];
  body.push(["Investment", ...hpeScenarios.map((s) => formatCurrency(s.input.alternativeInvestmentAmount))]);
  body.push(["Total Gross Return", ...hpeScenarios.map((s) => formatCurrency(s.results.hpeFutureValue))]);
  const periodYears = Math.max(...hpeScenarios.map(journeyHorizon));
  body.push([`Gross Return (${periodYears}-Yr)`, ...hpeScenarios.map((s) => formatMultiple(altGrossReturnMultiple(s)))]);
  body.push([`Annualized Return (${periodYears}-Yr)`, ...hpeScenarios.map((s) => formatPercent(altAnnualizedReturn(s)))]);
  body.push([
    "Tax Savings",
    ...hpeScenarios.map((s) => formatCurrency(baseScenario.results.totalTaxLiability - s.results.totalTaxLiability)),
  ]);
  if (showFutureTaxes) {
    body.push([
      "Est. Future Taxes Avoided",
      ...hpeScenarios.map((s) =>
        s.results.estimatedFutureTaxAvoided > 0 ? formatCurrency(s.results.estimatedFutureTaxAvoided) : "—",
      ),
    ]);
  }
  body.push([
    "Net Benefit",
    ...hpeScenarios.map((s) => {
      const inv = s.input.alternativeInvestmentAmount;
      const taxSavings = baseScenario.results.totalTaxLiability - s.results.totalTaxLiability;
      const netBenefit = taxSavings + s.results.hpeFutureValue - inv + s.results.estimatedFutureTaxAvoided;
      return formatCurrency(netBenefit);
    }),
  ]);

  autoTable(doc, {
    startY: y,
    head: [head],
    body,
    theme: "grid",
    headStyles: { fillColor: NAVY, textColor: WHITE, fontSize: 8, fontStyle: "bold", halign: "center" },
    bodyStyles: { fontSize: 8 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 55 },
      ...Object.fromEntries(hpeScenarios.map((_, i) => [i + 1, { halign: "right" as const }])),
    },
    margin: { left: MARGIN, right: MARGIN, bottom: MARGIN + FOOTER_HEIGHT },
    didParseCell(data) {
      if (data.section !== "body") return;
      const lastRowIdx = body.length - 1;
      if (data.row.index === lastRowIdx && data.column.index > 0) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [232, 240, 254];
        const text = data.cell.text[0] ?? "";
        const isNeg = text.startsWith("-") || text.startsWith("($");
        data.cell.styles.textColor = isNeg ? RED : GREEN;
      }
    },
  });
}

// ── Main Export Function ─────────────────────────────────────────────────────
export async function generateComparisonPDF(scenarios: TaxScenario[], view: ExportView = LEGACY_EXPORT_VIEW): Promise<void> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });

  drawPage1(doc, scenarios);    // Snapshot
  await drawPage2(doc, scenarios, view); // Cost Benefit (N-yr horizon) — bars or journey cards, as on screen
  drawRothPage(doc, scenarios); // All Roth projections — own page (skipped if no Roth)
  const y = drawDetailsPage(doc, scenarios); // Detailed Comparison
  drawAltKeyTakeaways(doc, scenarios, y);

  // Stamp the CYA footer on every page after all content is laid out.
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawCYAFooter(doc);
  }

  const clientName = sanitizeFilename(scenarios[0].input.name);
  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`TaxGenie-${clientName}-${dateStr}.pdf`);
}
